import { FC, useEffect, useState } from "react";
import SubmitItem from "../../../components/template/SubmitItem/SubmitItem";
import { FieldValues, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useLocation, Form } from "react-router-dom";
import { Toast } from "../../../components/atom/Toast/Toast";
import { EDIT_ROLE_URL, ROLE_URL } from "../../../utils/endpoints";
import { handleSubmitItem } from "../../../helpers/handleSubmitItem";
import { handleDeleteItem } from "../../../helpers/handleDeleteItem";
import { RolesItemSchema } from "../data/RolesItemSchema";
import RolesSubmitForm from "./RolesSubmitForm";
import { useAuth } from "../../../provider/AuthProvider";
import { AlertDialogConfig } from "../../../types/Components/Alert/AlertDialogConfig";
import api from "../../../utils/api";

const EditRole: FC = () => {
  const { addToast } = Toast();
  const navigate = useNavigate();
  const location = useLocation();
  const { role } = location.state || {};
  const [idItem] = useState<number>(role?.id || 0);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [title, setTitle] = useState<string>(role?.name || "");
  const [rolePermissions, setRolePermissions] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoaded, setIsLoaded] = useState(true);

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    resolver: zodResolver(RolesItemSchema),
    mode: "onChange",
    shouldUseNativeValidation: false,
  });

  const { auth } = useAuth();
  const user = auth.user;

  useEffect(() => {
    if (role) {
      setValue("name", role.name);
      setValue("organizationLevelOption", role.level);
      setValue("permissions", role.permissions);

      setTitle(role.name);
      setRolePermissions(role.permissions);
    }
  }, [role, setValue, rolePermissions]);

  const onSubmit = async (data: FieldValues) => {
    const bodyRequest = {
      id: idItem,
      name: data.name,
      roleLevelId: data.organizationLevelOption.id,
      roleTypeId: 1,
      permissions: data.permissions,
    };

    await handleSubmitItem(
      () => api.patch(EDIT_ROLE_URL, bodyRequest),
      addToast,
      setIsLoaded,
    );
    navigate("/perfis");
  };

  const handleDelete = () => {
    handleDeleteItem(ROLE_URL, setIsAlertOpen, idItem, addToast, setIsLoaded);
  };

  const alertDialogConfig: AlertDialogConfig = {
    setIsAlertOpen,
    isAlertOpen,
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <SubmitItem
        title={title}
        formTitle="Dados pessoais"
        handleSubmitItem={handleSubmit(onSubmit)}
        handleDeleteItem={
          user.permissions.includes("DeleteRole") ? handleDelete : undefined
        }
        deleteButtonName="Eliminar perfil"
        setIsEditing={setIsEditing}
        isEditing={isEditing}
        navigatePath="/perfis"
        alertDialogConfig={alertDialogConfig}
        isLoaded={isLoaded}
      >
        <RolesSubmitForm
          errors={errors}
          control={control}
          watch={watch}
          setValue={setValue}
          hasInteracted={hasInteracted}
          setHasInteracted={setHasInteracted}
          rolePermissions={rolePermissions}
          organizationLevelOptionSelected={role.level.id}
          isDisabled={!isEditing}
        />
      </SubmitItem>
    </Form>
  );
};

export default EditRole;
